import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Menu, X, Phone } from "lucide-react";

export function SiteHeader() {
  const [user, setUser] = useState<any>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [phone, setPhone] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    const check = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!mounted) return;
      setUser(user ?? null);
      if (!user) { setIsAdmin(false); }
      else {
        const { data } = await supabase.from("user_roles").select("role").eq("user_id", user.id).eq("role", "admin").maybeSingle();
        if (mounted) setIsAdmin(!!data);
      }
    };
    const loadPhone = async () => {
      const { data } = await supabase.from("settings").select("contact_phone").eq("id", 1).maybeSingle();
      if (mounted) setPhone(data?.contact_phone ?? null);
    };
    check();
    loadPhone();
    const { data: sub } = supabase.auth.onAuthStateChange(() => check());
    return () => { mounted = false; sub.subscription.unsubscribe(); };
  }, []);

  const nav = [
    { to: "/", label: "Home" },
    { to: "/fleet", label: "Fleet" },
    { to: "/how-it-works", label: "How it Works" },
    { to: "/about", label: "About" },
    { to: "/faq", label: "FAQ" },
    { to: "/contact", label: "Contact" },
  ];

  return (
    <header className="sticky top-0 z-40 border-b border-border/60 bg-cream/85 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
        <Link to="/" className="flex items-center gap-2">
          <div className="grid h-9 w-9 place-items-center rounded-full bg-primary text-primary-foreground font-display text-lg">C</div>
          <div className="font-display text-xl tracking-wide text-espresso">Creed Car Rental</div>
        </Link>
        <nav className="hidden items-center gap-8 md:flex">
          {nav.map(n => (
            <Link key={n.to} to={n.to} className="text-sm font-medium text-espresso/70 transition hover:text-accent">{n.label}</Link>
          ))}
          {isAdmin && <Link to="/admin" className="text-sm font-medium text-accent">Admin</Link>}
          {!user && <Link to="/auth" className="text-sm font-medium text-espresso/70 transition hover:text-accent">Sign In</Link>}
        </nav>
        <div className="hidden md:flex items-center gap-3">
          {phone && (
            <a href={`tel:${phone}`} className="flex items-center gap-1.5 text-sm font-medium text-espresso/80 hover:text-accent">
              <Phone className="h-4 w-4" /> {phone}
            </a>
          )}
          {user && (
            <button
              onClick={() => supabase.auth.signOut()}
              className="text-sm font-medium text-espresso/70 transition hover:text-accent"
            >
              Sign Out
            </button>
          )}
          <Button asChild className="bg-primary hover:bg-primary/90"><Link to="/fleet">Book a car</Link></Button>
        </div>
        <div className="flex items-center gap-1 md:hidden">
          {phone && (
            <a href={`tel:${phone}`} className="p-2 text-accent" aria-label="Call us">
              <Phone className="h-5 w-5" />
            </a>
          )}
          <button className="p-2" onClick={() => setOpen(!open)} aria-label="Menu">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="border-t border-border/60 bg-cream md:hidden">
          <div className="flex flex-col p-4 gap-3">
            {nav.map(n => (
              <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="py-2 text-espresso">{n.label}</Link>
            ))}
            {isAdmin && <Link to="/admin" onClick={() => setOpen(false)} className="py-2 text-accent">Admin</Link>}
            {!user && <Link to="/auth" onClick={() => setOpen(false)} className="py-2 text-espresso">Sign In</Link>}
            {user && (
              <button onClick={() => { supabase.auth.signOut(); setOpen(false); }} className="py-2 text-left text-espresso">Sign Out</button>
            )}
            {phone && (
              <a href={`tel:${phone}`} className="py-2 text-accent flex items-center gap-2"><Phone className="h-4 w-4" /> {phone}</a>
            )}
            <Button asChild className="mt-2"><Link to="/fleet" onClick={() => setOpen(false)}>Book a car</Link></Button>
          </div>
        </div>
      )}
    </header>
  );
}
